import type { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm';
import { TYPES } from '@/engine/ioc/types';
import { useInjection } from 'inversify-react';
import { useCallback, useEffect, useState } from 'react';
import * as DT from '@/engine/PlanningAlgorithm/DataTypes';
import type { IDisposable } from '@/engine/Utilities/IDisposable';
import type { IValue } from '@/engine/DataFlow/Graph';

function _getCriteriaRangeMessage(
  range: DT.CriteriaRangeT | undefined,
  uom: string | undefined,
  factor = 1
): string | undefined {
  if (!range) return undefined;
  if (range.greaterThan !== undefined) {
    if (range.lessThan !== undefined) {
      return `> ${(range.greaterThan * factor).toFixed()} ${uom} and < ${(range.lessThan * factor).toFixed()} ${uom}`;
    } else {
      return `> ${(range.greaterThan * factor).toFixed()} ${uom}`;
    }
  } else if (range.lessThan !== undefined) {
    return `< ${(range.lessThan * factor).toFixed()} ${uom}`;
  } else {
    console.warn('At least one range should be defined');
    return undefined;
  }
}

function _getCriteriaMessage(
  criteria: DT.CheckCriteriaT | undefined,
  uom: string | undefined,
  factor = 1
): Array<{ text: string; icon?: 'success' | 'error' | 'warning' }> | undefined {
  if (!criteria) return;
  const yesMessage = _getCriteriaRangeMessage(criteria.yesRange, uom, factor);
  const noMessage = _getCriteriaRangeMessage(criteria.noRange, uom, factor);
  const maybeMessage = _getCriteriaRangeMessage(criteria.maybeRange, uom, factor);
  if (yesMessage !== undefined) {
    if (noMessage !== undefined) {
      if (maybeMessage !== undefined) {
        return [
          { text: `Success: ${yesMessage}`, icon: 'success' },
          { text: `Warning: ${maybeMessage}`, icon: 'warning' },
          { text: `Error: ${noMessage}`, icon: 'error' }
        ];
      } else {
        return [
          { text: `Success: ${yesMessage}`, icon: 'success' },
          { text: `Error: ${noMessage}`, icon: 'error' },
          { text: `Warning: otherwise`, icon: 'warning' }
        ];
      }
    } else {
      if (maybeMessage !== undefined) {
        return [
          { text: `Success: ${yesMessage}`, icon: 'success' },
          { text: `Warning: ${maybeMessage}`, icon: 'warning' },
          { text: `Error: otherwise`, icon: 'error' }
        ];
      } else {
        if (criteria.defaultStatus === DT.CheckStatusT.No) {
          return [
            { text: `Success: ${yesMessage}`, icon: 'success' },
            { text: `Error: otherwise`, icon: 'error' }
          ];
        } else {
          return [
            { text: `Success: ${yesMessage}`, icon: 'success' },
            { text: `Warning: otherwise`, icon: 'warning' }
          ];
        }
      }
    }
  } else {
    if (noMessage !== undefined) {
      if (maybeMessage !== undefined) {
        return [
          { text: `Error: ${noMessage}`, icon: 'error' },
          { text: `Warning: ${maybeMessage}`, icon: 'warning' },
          { text: `Success: otherwise`, icon: 'success' }
        ];
      } else {
        if (criteria.defaultStatus === DT.CheckStatusT.Yes) {
          return [
            { text: `Error: ${noMessage}`, icon: 'error' },
            { text: `Success: otherwise`, icon: 'success' }
          ];
        } else {
          return [
            { text: `Error: ${noMessage}`, icon: 'error' },
            { text: `Warning: otherwise`, icon: 'warning' }
          ];
        }
      }
    } else {
      if (maybeMessage !== undefined) {
        if (criteria.defaultStatus === DT.CheckStatusT.Yes) {
          return [
            { text: `Warning: ${maybeMessage}`, icon: 'warning' },
            { text: `Success: otherwise`, icon: 'success' }
          ];
        } else {
          return [
            { text: `Warning: ${maybeMessage}`, icon: 'warning' },
            { text: `Error: otherwise`, icon: 'error' }
          ];
        }
      } else {
        console.warn('At least one criteria should be defined');
        return undefined;
      }
    }
  }
}

function _getCriteriaStatus(criteria: DT.CheckCriteriaT | undefined): 'success' | 'warning' | 'error' | undefined {
  if (!criteria) return undefined;
  if (criteria.status === DT.CheckStatusT.Yes) {
    return 'success';
  } else if (criteria.status === DT.CheckStatusT.Maybe) {
    return 'warning';
  } else if (criteria.status === DT.CheckStatusT.No) {
    return 'error';
  } else {
    console.warn('Unknown criteria status');
    return undefined;
  }
}

export const useCriteria = (
  descriptor: string,
  uom?: string,
  factor = 1
): {
  criteriaStatus: 'success' | 'warning' | 'error' | undefined;
  criteriaMessage: Array<{ text: string; icon?: 'success' | 'error' | 'warning' }> | undefined;
} => {
  const planningAlgorithm = useInjection<PlanningAlgorithm>(TYPES.PlanningAlgorithm);

  const [criteriaStatus, setCriteriaStatus] = useState<'success' | 'warning' | 'error' | undefined>(undefined);
  const [criteriaMessage, setCriteriaMessage] = useState<
    Array<{ text: string; icon?: 'success' | 'error' | 'warning' }> | undefined
  >(undefined);

  const handleValueChanged = useCallback(
    (value?: DT.IWithCriteria): void => {
      const criteria: DT.CheckCriteriaT | undefined = value?.getCriteria();
      setCriteriaStatus(_getCriteriaStatus(criteria));
      setCriteriaMessage(_getCriteriaMessage(criteria, uom, factor));
    },
    [uom, factor]
  );

  useEffect(() => {
    let subscription: IDisposable | undefined;

    if (planningAlgorithm.has(descriptor)) {
      subscription = planningAlgorithm.addOnChangedCallbackFor<IValue & DT.IWithCriteria>(
        descriptor,
        handleValueChanged
      );

      const criteria = (
        planningAlgorithm.getIValue(descriptor) as unknown as DT.IWithCriteria | undefined
      )?.getCriteria();

      setCriteriaStatus(_getCriteriaStatus(criteria));
      setCriteriaMessage(_getCriteriaMessage(criteria, uom, factor));
    }

    return (): void => {
      subscription?.dispose();
      subscription = undefined;
    };
  }, [handleValueChanged, planningAlgorithm, descriptor, uom, factor]);

  return { criteriaStatus: criteriaStatus, criteriaMessage: criteriaMessage };
};
